import { useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FileDown, FileSpreadsheet, Sparkles, Upload } from "lucide-react";
import { toast } from "sonner";
import { api, apiBlob, apiUpload, downloadBlob } from "../lib/api";
import { PageHeader, Panel } from "../components/common";
import { defaultPeriod, MESI, PeriodPicker } from "../components/PeriodPicker";
import { Button } from "../components/ui/button";

export default function AdminShifts() {
  const nav = useNavigate();
  const [p, setP] = useState(defaultPeriod());
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState("");
  const [result, setResult] = useState(null);
  const ref = useRef();
  const label = `${MESI[p.mese - 1]} ${p.anno} · ${p.reparto}`;
  const viewUrl = `/admin/shifts-view?reparto=${p.reparto}&mese=${p.mese}&anno=${p.anno}`;

  const generate = async () => {
    if (!window.confirm(`Attenzione: sostituisce i turni esistenti per ${label}. Continuare?`)) return;
    setBusy("gen");
    try {
      const r = await api("/admin/shifts/generate", { method: "POST", body: { reparto: p.reparto, mese: p.mese, anno: p.anno } });
      toast.success(`Turni generati: ${r.created} (${r.dipendenti} dipendenti)`);
      nav(viewUrl);
    } catch (e) { toast.error(e.message || "Errore generazione"); } finally { setBusy(""); }
  };

  const template = async () => {
    setBusy("tpl");
    try {
      const res = await apiBlob(`/web/shifts/template?reparto=${p.reparto}&mese=${p.mese}&anno=${p.anno}`);
      downloadBlob(await res.blob(), `modello_turni_${p.reparto.toLowerCase()}_${p.mese}_${p.anno}.xlsx`);
      toast.success("Modello XLS scaricato");
    } catch (e) { toast.error(e.message); } finally { setBusy(""); }
  };

  const upload = async () => {
    if (!file) return toast.error("Seleziona un file");
    setBusy("up");
    try {
      const form = new FormData();
      form.append("file", file, file.name);
      form.append("reparto", p.reparto);
      form.append("mese", String(p.mese));
      form.append("anno", String(p.anno));
      const d = await apiUpload("/admin/shifts/upload", form);
      setResult(d);
      toast.success(`${d.created} turni caricati`);
    } catch (e) { toast.error(e.message); } finally { setBusy(""); }
  };

  return (
    <div className="max-w-3xl">
      <PageHeader subtitle="Genera in automatico o carica un foglio mensile" title="Genera / Carica Turni" actions={<Button asChild variant="outline" className="rounded-full" data-testid="shifts-view-link"><Link to={viewUrl}>Turni Caricati</Link></Button>} />
      <Panel className="mb-6"><PeriodPicker p={p} setP={setP} /></Panel>

      <Panel className="mb-6 space-y-3">
        <div className="flex items-center gap-3"><Sparkles className="w-5 h-5 text-[var(--teal)]" /><h2 className="font-display font-bold text-lg text-[var(--navy)]">Genera turni automaticamente</h2></div>
        <p className="text-sm text-slate-600">Crea una bozza dei turni del mese ({label}). Attenzione: sostituisce i turni esistenti per {label}.</p>
        <Button onClick={generate} disabled={!!busy} className="rounded-full bg-[var(--navy)]" data-testid="shifts-generate-button">{busy === "gen" ? "Generazione..." : "Genera turni"}</Button>
      </Panel>

      <Panel className="space-y-5">
        <div className="flex items-center gap-3"><Upload className="w-5 h-5 text-[var(--teal)]" /><h2 className="font-display font-bold text-lg text-[var(--navy)]">Carica Turni da file</h2></div>
        <div className="rounded-xl bg-[var(--teal-soft)] p-4 flex flex-wrap items-center justify-between gap-3">
          <div><p className="font-medium text-sm text-[var(--navy)]">File modello XLS</p><p className="text-xs text-slate-600">Una riga per giorno del mese, una colonna per dipendente (cognome). Compila gli orari (es. 09.00-17.00, Riposo, Ferie) e ricaricalo qui.</p></div>
          <Button variant="outline" onClick={template} disabled={!!busy} className="rounded-full bg-white" data-testid="shifts-template-download-button"><FileDown className="w-4 h-4 mr-1" />{busy === "tpl" ? "Preparazione..." : "Scarica modello XLS"}</Button>
        </div>
        <button type="button" onClick={() => ref.current.click()} className="w-full rounded-xl border-2 border-dashed border-slate-300 hover:border-[var(--teal)] p-8 flex flex-col items-center text-slate-500 transition-colors" data-testid="shifts-file-picker">
          <FileSpreadsheet className="w-8 h-8 mb-2" />{file ? <span className="font-medium text-[var(--navy)]">{file.name}</span> : "Clicca per selezionare un file CSV/XLSX"}
        </button>
        <input ref={ref} type="file" hidden accept=".csv,.xlsx,.xls" onChange={(e) => { setFile(e.target.files?.[0] || null); setResult(null); }} data-testid="shifts-file-input" />
        <Button onClick={upload} disabled={!!busy} className="rounded-full bg-[var(--teal)] px-8" data-testid="shifts-upload-button">{busy === "up" ? "Caricamento..." : "Carica Turni"}</Button>
        {result && (
          <div className="rounded-xl border p-4 text-sm" data-testid="shifts-upload-result">
            <p className="font-semibold text-emerald-700">{result.created} turni caricati per {label}</p>
            {result.not_found?.length > 0 && <p className="text-amber-700 mt-1">Cognomi non riconosciuti: {result.not_found.join(", ")}</p>}
            <Link to={viewUrl} className="text-[var(--teal)] font-semibold mt-2 inline-block">Vai ai turni caricati</Link>
          </div>
        )}
      </Panel>
    </div>
  );
}
